import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { setTrackContext } from './track';

// "Who is picking?" — shown when the picking list (or a packing session) is
// started on a shared tablet account (users.is_device). The WMS knows who moved
// these orders to Printed, so that name is offered first; then everyone else
// on the floor. On a person's own login the PICKING list asks one thing: "Pick these
// orders" (you become the picker — "Take over from Natalia" when someone else is) or
// "Just look" (nothing is recorded; Fernando, 2026-10-05, after opening the list to look
// made him the picker of Natalia's round 141). Packing on a personal login records the
// person silently, as before. (Fernando, 2026-09-07)
//
// Resolves the round for the selected orders first (merging batches picked
// together), then records the choice on it, then hands round + person to the
// caller — the picking window and the tracking buffer both need them.

interface Person { username: string; name: string; lang?: string }

interface Props {
  orderIds: string[];
  role: 'picker' | 'packer';
  /** viewOnly: "Just look" — the picking screen records nothing */
  onReady: (ctx: { roundId: number | null; actor: string | null; viewOnly?: boolean }) => void;
  onCancel: () => void;
}

export default function WhoIsPicking({ orderIds, role, onReady, onCancel }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roundId, setRoundId] = useState<number | null>(null);
  const [starters, setStarters] = useState<string[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [saving, setSaving] = useState(false);
  // a person's own login on the picking list: the choice "Pick these orders" / "Just look"
  // `guided`: the round is walked on the P&P screen's cart tablet — here it can only be looked at (Fernando 3.11)
  const [ask, setAsk] = useState<{ picker: string | null; me: string | null; meLang: string | null;
                                   guided: number | null } | null>(null);
  const personalPicker = role === 'picker' && !localStorage.getItem('wms_is_device');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rr, pr] = await Promise.all([
          authFetch(`${API}/pp/rounds/resolve`, {
            method: 'POST',
            // a personal login on the picking list only LOOKS first (nothing merged or started) —
            // the round is resolved for real when the person says they are picking
            body: JSON.stringify({ order_ids: orderIds, flow: localStorage.getItem('wms_pp_flow') || 'default',
                                   view: personalPicker }),
          }),
          authFetch(`${API}/pp/people`),
        ]);
        let rd = await rr.json().catch(() => ({} as any));
        const pd = await pr.json().catch(() => ({} as any));
        if (!alive) return;
        if (!rr.ok) { setError(rd.detail || 'Could not find the round for these orders'); setLoading(false); return; }
        if (rd.view && rd.is_device) {
          // the stored login type was stale — a tablet resolves the round for real (merge / start)
          const r2 = await authFetch(`${API}/pp/rounds/resolve`, {
            method: 'POST', body: JSON.stringify({ order_ids: orderIds, flow: localStorage.getItem('wms_pp_flow') || 'default' }),
          });
          rd = await r2.json().catch(() => ({} as any));
          if (!alive) return;
          if (!r2.ok) { setError(rd.detail || 'Could not find the round for these orders'); setLoading(false); return; }
        }
        const rid: number | null = rd.round ? rd.round.id : null;
        setRoundId(rid);
        setStarters(rd.starters || []);
        setPeople(pd.people || []);
        if (!rd.is_device && role === 'picker') {
          const meLang = (pd.people || []).find((p: Person) => p.username === rd.login_person)?.lang || null;
          setAsk({ picker: rd.round?.picker_username || null, me: rd.login_person || null, meLang,
                   guided: rd.round?.guided ? rd.round.id : null });
          setLoading(false);
          return;
        }
        if (!rd.is_device) {
          // A person's own login packing: record them and go — nothing to ask.
          if (rid) {
            await authFetch(`${API}/pp/rounds/${rid}/picker`, { method: 'POST', body: JSON.stringify({ role }) }).catch(() => {});
          }
          const meLang = (pd.people || []).find((p: Person) => p.username === rd.login_person)?.lang || null;
          setTrackContext({ roundId: rid, actor: rd.login_person || null, lang: meLang });
          onReady({ roundId: rid, actor: rd.login_person || null });
          return;
        }
        setLoading(false);
      } catch {
        if (alive) { setError('Could not reach the WMS'); setLoading(false); }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async (username: string) => {
    if (saving) return;
    setSaving(true);
    try {
      if (roundId) {
        const r = await authFetch(`${API}/pp/rounds/${roundId}/picker`, {
          method: 'POST', body: JSON.stringify({ username, role }),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({} as any));
          setError(d.detail || 'Could not record the name'); setSaving(false); return;
        }
      }
      // the instructions on the tablet follow the PERSON's language, not the device login's
      setTrackContext({ roundId, actor: username, lang: people.find(p => p.username === username)?.lang || null });
      onReady({ roundId, actor: username });
    } catch {
      setError('Could not reach the WMS'); setSaving(false);
    }
  };

  // "Pick these orders": resolve the round for real (merge / start), then record the person —
  // taking the round over only when the screen showed someone else picking it
  const pickMyself = async () => {
    if (saving || !ask) return;
    setSaving(true); setError('');
    try {
      const rr = await authFetch(`${API}/pp/rounds/resolve`, {
        method: 'POST', body: JSON.stringify({ order_ids: orderIds, flow: localStorage.getItem('wms_pp_flow') || 'default' }),
      });
      const rd = await rr.json().catch(() => ({} as any));
      if (!rr.ok) { setError(rd.detail || 'Could not find the round for these orders'); setSaving(false); return; }
      const rid: number | null = rd.round ? rd.round.id : null;
      const takeOver = !!(ask.picker && ask.picker !== ask.me);
      if (rid) {
        const r = await authFetch(`${API}/pp/rounds/${rid}/picker`, { method: 'POST', body: JSON.stringify({ role, take_over: takeOver }) });
        if (r.status === 409) {
          const d = await r.json().catch(() => ({} as any));
          setAsk(a => a ? { ...a, picker: d.detail?.picker || a.picker } : a);
          setError(d.detail?.message || 'Someone else is picking this round');
          setSaving(false); return;
        }
      }
      setTrackContext({ roundId: rid, actor: rd.login_person || ask.me, lang: ask.meLang });
      onReady({ roundId: rid, actor: rd.login_person || ask.me });
    } catch {
      setError('Could not reach the WMS'); setSaving(false);
    }
  };
  // "Just look": nothing is recorded — no picker, no round merge, no tracking
  const justLook = () => onReady({ roundId, actor: ask?.me || null, viewOnly: true });

  const nameOf = (u: string) => people.find(p => p.username === u)?.name || u;
  const others = people.filter(p => !starters.includes(p.username));
  const verb = role === 'picker' ? 'picking' : 'packing';

  return (
    <div className="fixed inset-0 bg-[#0f1117] z-50 flex flex-col">
      <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-6 py-4 flex items-center gap-4">
        <button onClick={onCancel} className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">✕ Cancel</button>
        <span className="text-white font-bold">{ask ? 'Picking list' : `Who is ${verb}?`}</span>
        <span className="text-gray-500 text-sm">{orderIds.length} orders</span>
      </div>
      <div className="flex-1 overflow-y-auto px-8 py-8">
        {loading && <div className="text-gray-400 text-center mt-20">Finding the round…</div>}
        {error && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2 max-w-lg mx-auto text-center mb-6">{error}</div>}
        {!loading && ask && (
          <div className="max-w-xl mx-auto space-y-4">
            {ask.picker && ask.picker !== ask.me && (
              <div className="text-amber-200 text-sm bg-amber-900/20 border border-amber-800/40 rounded-lg px-4 py-3 text-center">
                {nameOf(ask.picker)} is picking this round.
              </div>
            )}
            {ask.guided && (
              <div className="text-gray-300 text-sm bg-gray-800/60 border border-gray-700 rounded-lg px-4 py-3 text-center">
                These orders are in round {ask.guided}. It is picked with the P&amp;P screen's cart tablet — here you can only look.
              </div>
            )}
            <button onClick={justLook} disabled={saving}
              className="w-full py-6 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white text-2xl font-bold rounded-2xl border border-gray-700">
              👁 Just look
              <div className="text-sm font-normal text-gray-400 mt-1">See the list — nothing is recorded, nobody's round changes</div>
            </button>
            {!ask.guided && (
              <button onClick={pickMyself} disabled={saving}
                className="w-full py-6 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-2xl font-black rounded-2xl">
                {ask.picker && ask.picker !== ask.me ? `Take over from ${nameOf(ask.picker)}` : 'Pick these orders'}
                <div className="text-sm font-normal text-blue-100/80 mt-1">You become the picker of this round</div>
              </button>
            )}
          </div>
        )}
        {!loading && !ask && (
          <div className="max-w-3xl mx-auto space-y-8">
            {starters.length > 0 && (
              <div>
                <div className="text-gray-500 text-xs uppercase tracking-widest mb-3">Moved these orders to Printed</div>
                <div className="grid grid-cols-2 gap-4">
                  {starters.map(u => (
                    <button key={u} onClick={() => choose(u)} disabled={saving}
                      className="py-8 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-3xl font-black rounded-2xl">
                      {nameOf(u)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="text-gray-500 text-xs uppercase tracking-widest mb-3">
                {starters.length ? 'No — somebody else is helping' : 'Pick your name'}
              </div>
              <div className="grid grid-cols-3 keep-cols gap-3">
                {others.map(p => (
                  <button key={p.username} onClick={() => choose(p.username)} disabled={saving}
                    className="py-5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white text-xl font-bold rounded-xl border border-gray-700">
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
